/**
 * Pure helpers for the Pool Audit log tab (PoolAuditPage.tsx / AuditDetailModal.tsx) — the action string
 * → i18n label map, and the `details` JSON → human lines renderer. Kept pure/side-effect-free so both the
 * table cell and the detail modal call the exact same rendering, and so it's unit-testable without
 * mounting anything.
 *
 * The action list is the CANONICAL set every `insert_audit(...)` call site in the API repo's
 * `app/modules/pool/` actually writes (audit.py's own `OBSERVED_ACTIONS` constant covers the sweep half;
 * the rest is every literal `action=` in service.py/intents.py/expiry.py — grepped 21/7, since the BE's
 * `/audit` filter contract was still being extended on the same branch and `list_audit` carried no
 * committed constant of its own yet). An action this build has never heard of still renders — see
 * auditActionLabel's fallback — so a new action type added on the API side later doesn't render a blank
 * cell, just an untranslated raw string until this map is updated.
 */
import type { TKey } from "@/i18n/keys";

/** Raw `pool.audit_log.action` string → its i18n key. A `Record<string, TKey>` (not a narrower union)
 *  on purpose — the server's action vocabulary isn't exhaustively typed anywhere on the FE (see the
 *  `action: string` banner on PoolAuditEntry's sibling ActivityItem.event_code for the same reasoning),
 *  so a lookup miss is an ordinary, expected case handled by auditActionLabel's fallback. */
export const AUDIT_ACTION_LABEL_KEY: Record<string, TKey> = {
  "pool.add": "pool.audit.actionPoolAdd",
  "pool.enable": "pool.audit.actionPoolEnable",
  "pool.disable": "pool.audit.actionPoolDisable",
  // rev 7 (added alongside RemovePoolAccountDialog.tsx) — missed here until the rev 8 screenshot review
  // caught it rendering RAW ("account.remove") in the Action column.
  "account.remove": "pool.audit.actionAccountRemove",
  // rev 15.2/16 — same raw-render class as account.remove above: both shipped without a matching
  // entry here, caught while adding manual_info's own label (same "grep the map against the real
  // service.py action= literals" review this file's banner already prescribes).
  "account.subscription_period": "pool.audit.actionAccountSubscriptionPeriod",
  "account.manual_info": "pool.audit.actionAccountManualInfo",
  // rev 15 — the standalone "Test connection" button; also missed by the same sweep as the two
  // above (adversarial review, 23/7 — a grep for "grep the map against the real action= literals"
  // that itself wasn't exhaustive the first time).
  "credential.test": "pool.audit.actionCredentialTest",
  // task A, 22/7 — one email observed in >1 swept account the same round (audit.py's
  // OBSERVED_ACTIONS correctly includes it; only this FE label was missing).
  "observed.conflict": "pool.audit.actionObservedConflict",
  // r8 verify lens: the Sync Now button writes this the moment an operator clicks it — same
  // raw-render class as account.remove; label added the same day the action shipped this time.
  "sweep.manual_trigger": "pool.audit.actionSweepManualTrigger",
  "period.create": "pool.audit.actionPeriodCreate",
  "period.update": "pool.audit.actionPeriodUpdate",
  "period.close": "pool.audit.actionPeriodClose",
  "renew.set": "pool.audit.actionRenewSet",
  "operator.grant": "pool.audit.actionOperatorGrant",
  "operator.revoke": "pool.audit.actionOperatorRevoke",
  "system.state_change": "pool.audit.actionSystemStateChange",
  "intent.resolve": "pool.audit.actionIntentResolve",
  "force.license_remove": "pool.audit.actionForceLicenseRemove",
  "force.unlink": "pool.audit.actionForceUnlink",
  // r1 critique (24/7, prod screenshot): the bulk-force endpoint's own write (BulkForceBar/
  // BulkForceDialog's chunked "POST /pool/users/bulk-force" calls) rendered raw ("force.bulk") in the
  // Action column — same missed-label class as account.remove/credential.test above.
  "force.bulk": "pool.audit.actionForceBulk",
  "license.remove": "pool.audit.actionLicenseRemove",
  "user.unlink": "pool.audit.actionUserUnlink",
  "observed.moved": "pool.audit.actionObservedMoved",
  "observed.joined": "pool.audit.actionObservedJoined",
  "observed.left_estate": "pool.audit.actionObservedLeftEstate",
  "observed.license_removed": "pool.audit.actionObservedLicenseRemoved",
  "observed.license_granted": "pool.audit.actionObservedLicenseGranted",
  "observed.renamed": "pool.audit.actionObservedRenamed",
  "observed.recreated": "pool.audit.actionObservedRecreated",
  "observed.rename_conflict": "pool.audit.actionObservedRenameConflict",
  "observed.blank_email": "pool.audit.actionObservedBlankEmail",
  // 30/7 — the nightly (23:00) daily-snapshot job behind the Reports tab (PoolReportsPage); actor
  // "system" (not "sweep" — a separate cron path from the 5-minute membership sweep).
  "snapshot.daily": "pool.audit.actionSnapshotDaily",
};

/** Every action string this build knows a label for — the action filter's Select options. */
export const AUDIT_ACTIONS: readonly string[] = Object.keys(AUDIT_ACTION_LABEL_KEY).sort();

/** Human label for an action string, or the raw string itself when unmapped (never a blank cell). */
export function auditActionLabel(t: (key: TKey) => string, action: string): string {
  const key = AUDIT_ACTION_LABEL_KEY[action];
  return key ? t(key) : action;
}

const MAX_VALUE_CHARS = 40;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A single `{field, old, new}` entry — every real `insert_audit(..., details=[...])` call site logs an
 *  ARRAY of exactly this shape (see this file's own banner). `field` is a plain string key name; `old`/
 *  `new` are whatever JSON value that field held (string, number, boolean, or null). */
function isFieldChange(v: unknown): v is { field: unknown; old?: unknown; new?: unknown } {
  return isPlainObject(v) && "field" in v;
}

function truncate(s: string): string {
  return s.length > MAX_VALUE_CHARS ? `${s.slice(0, MAX_VALUE_CHARS)}…` : s;
}

/** Render one JSON value compactly — null/undefined as an em dash (never "null"/"undefined" literally),
 *  everything else stringified and truncated so one long value can't blow out a table cell/detail line. */
function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return truncate(s);
}

/**
 * Render one VALUE inside a plain-object `details` payload (formatAuditDetails' isPlainObject branch) —
 * used for a nested object/array field so it never falls through to fmtValue's raw JSON.stringify, which
 * is exactly how a bulk-force row's `outcomes` tally rendered as unreadable truncated JSON in the table
 * cell ("outcomes: {"ok_removed":3,"fail…", r1 critique 24/7). A TALLY — every value a number, e.g.
 * `{ok_removed: 3, failed_forbidden: 1}` — reads as "ok_removed: 3 · failed_forbidden: 1"; anything else
 * nested falls back to a plain count ("N items"/"N fields") rather than dumping raw JSON into a cell — the
 * AuditDetailModal's <pre> is still the place for the real, full payload.
 */
function summarizeNested(v: unknown): string {
  if (Array.isArray(v)) {
    return v.length === 1 ? "1 item" : `${v.length} items`;
  }
  if (isPlainObject(v)) {
    const entries = Object.entries(v);
    if (entries.length === 0) return "—";
    if (entries.every(([, val]) => typeof val === "number")) {
      return truncate(entries.map(([k, val]) => `${k}: ${val}`).join(" · "));
    }
    return entries.length === 1 ? "1 field" : `${entries.length} fields`;
  }
  return fmtValue(v);
}

/**
 * account_id → `{name, number}` for the CURRENT pool estate — usePoolAccountLookup (hooks.ts) is the one
 * real implementation, built off usePoolAccounts. Threaded in as a plain function (not a hook import
 * here) so this file stays pure/hookless and unit-testable without mounting anything — same reasoning as
 * the rest of this file's banner.
 */
export type AccountLookup = (
  accountId: string,
) => { name: string | null; number: string | null } | undefined;

/**
 * r10 22/7 (principal feedback, prod screenshot): an `account_id` detail change used to render the raw
 * Zoom account id verbatim ("account_id: G_dBbXGUQkaKQDvdnw33Pw → …") — unreadable to an operator who
 * only knows accounts by name/number. Two sources can resolve it to "name (#number)": the BE now attaches
 * `{old,new}_account_{name,number}` straight onto the change item for its own two-phase writes (parallel
 * api-repo task, same branch) — PREFERRED, since it's resolved at write time and survives the account
 * later being removed from the pool — falling back to `lookup`, which only knows accounts still in the
 * pool today. Neither found → the raw id, never blank.
 */
function humanizeAccountId(
  raw: unknown,
  shippedName: unknown,
  shippedNumber: unknown,
  lookup: AccountLookup | undefined,
): { text: string; humanized: boolean } {
  if (typeof raw !== "string" || raw === "") return { text: fmtValue(raw), humanized: false };
  const found = lookup?.(raw);
  const name = (typeof shippedName === "string" && shippedName) || found?.name || null;
  const number = (typeof shippedNumber === "string" && shippedNumber) || found?.number || null;
  if (!name) return { text: raw, humanized: false };
  return { text: truncate(number ? `${name} (#${number})` : name), humanized: true };
}

/** null/undefined/"" all read as "nothing here" for the purposes of a change line — a blank string is
 *  exactly as uninformative as a JSON null (rev 8, prod screenshot review). */
function isEmptyChangeValue(v: unknown): boolean {
  return v === null || v === undefined || v === "";
}

/** Zoom `user_type` → the operator vocabulary the License pill already uses. 1/2 are the only values
 *  the pool estate carries (3=on-prem never appears here) — anything else returns null → raw render. */
function humanizeUserType(v: unknown): string | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v !== "" ? Number(v) : NaN;
  if (n === 1) return "Basic";
  if (n === 2) return "Licensed";
  return null;
}

/**
 * One `field: …` change line, or `null` to render NO line at all (rev 8, prod screenshot review — a
 * `has_managed_domain: — → —` line with both sides empty told the operator nothing and was dropped).
 * `old`/`new` are never BOTH rendered as dashes flanking an arrow: an arrow only appears when both sides
 * carry a real value. A creation-shaped change (`old` empty, `new` set) reads as `field: new` — never
 * `field: — → new`; a removal-shaped one (`old` set, `new` empty) reads as `field: old` — never
 * `field: old → —`. Only when BOTH sides are real values does the arrow appear.
 *
 * `item` (not just `field`/`old`/`new`) so an `account_id` change can also read its BE-shipped
 * `{old,new}_account_{name,number}` sibling keys — see humanizeAccountId. The label itself flips from
 * "account_id" to "account" ONLY when at least one side actually resolved to a name (r10 22/7) — an
 * unresolved id still gets the accurate "account_id" label, since what's showing IS the raw id.
 */
function fieldChangeLine(
  item: { field: unknown; old?: unknown; new?: unknown },
  resolveAccount?: AccountLookup,
): string | null {
  const field = String(item.field);
  const oldV = item.old;
  const newV = item.new;
  const oldEmpty = isEmptyChangeValue(oldV);
  const newEmpty = isEmptyChangeValue(newV);
  if (oldEmpty && newEmpty) return null;

  let label = field;
  let oldText = fmtValue(oldV);
  let newText = fmtValue(newV);

  if (field === "user_type") {
    // r14 22/7 (principal feedback, first live force): "user_type: 2 → 1" is Zoom-API-speak — an
    // operator reads licenses as Licensed/Basic (the same words the License pill uses), never raw
    // type numbers. Unknown numbers still render raw (accurate beats pretty).
    label = "license";
    oldText = humanizeUserType(oldV) ?? oldText;
    newText = humanizeUserType(newV) ?? newText;
  }

  if (field === "account_id") {
    const extra = item as Record<string, unknown>;
    const oldRes = oldEmpty
      ? null
      : humanizeAccountId(oldV, extra.old_account_name, extra.old_account_number, resolveAccount);
    const newRes = newEmpty
      ? null
      : humanizeAccountId(newV, extra.new_account_name, extra.new_account_number, resolveAccount);
    if (oldRes) oldText = oldRes.text;
    if (newRes) newText = newRes.text;
    if (oldRes?.humanized || newRes?.humanized) label = "account";
  }

  if (oldEmpty) return `${label}: ${newText}`;
  if (newEmpty) return `${label}: ${oldText}`;
  return `${label}: ${oldText} → ${newText}`;
}

/**
 * Compact human rendering of an audit row's `details` payload, as a list of one-line strings — e.g.
 * `["period_start: 2026-01-01 → 2026-02-01"]`. The primary (and, per this file's banner, the ONLY shape
 * any current write site produces) case is an array of `{field, old, new}` — see fieldChangeLine for the
 * empty/creation/removal rendering rules a plain `old → new` join used to get wrong. Two fallbacks exist
 * purely defensively, so an unfamiliar future payload still reads as *something* instead of
 * "[object Object]" or a crash: a plain array of values, and a plain `{key: value}` object.
 */
export function formatAuditDetails(details: unknown, resolveAccount?: AccountLookup): string[] {
  if (details === null || details === undefined) return [];
  if (Array.isArray(details)) {
    if (details.every(isFieldChange)) {
      return details
        .map((d) => fieldChangeLine(d, resolveAccount))
        .filter((line): line is string => line !== null);
    }
    return details.map((v, i) => `[${i}]: ${fmtValue(v)}`);
  }
  if (isPlainObject(details)) {
    return Object.entries(details).map(([k, v]) => `${k}: ${summarizeNested(v)}`);
  }
  return [fmtValue(details)];
}
